import { useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import LineFacebookIcon from 'LuxKit/Icons/line/LineFacebookIcon'
import config from 'constants/config'
import { fireInteractionEvent, fireNonInteractionEvent } from 'api/googleTagManager'
import TextButton from 'LuxKit/Button/TextButton'
import { SOCIAL_LOGIN_ICON_BUTTON_PROPS, SOCIAL_LOGIN_TEXT_BUTTON_PROPS, type SocialLoginButtonVariant } from './SocialLoginButton.constants'
import IconButton from 'LuxKit/Button/IconButton'
import { trackClientEvent } from 'analytics/analytics'
import type { FacebookLoginData } from 'actions/AuthActions'
import { ensureFacebookSdk, isFacebookSdkReady } from 'lib/auth/facebookSdk'
import LoadingSpinner from 'components/Common/Loading/LoadingSpinner'
import waitForSocialSdk, { SocialSdkTimeoutError } from 'lib/auth/waitForSocialSdk'
import noop from 'lib/function/noop'

interface Props {
  className?: string
  isBlocked?: boolean
  iconOnly?: boolean
  onBlockedClick?: () => void
  onSuccess: (data: FacebookLoginData) => void
  onFailure?: (data: { error: string }) => void
  variant?: SocialLoginButtonVariant
}

function FacebookLoginButton(props: Props) {
  const {
    isBlocked,
    onBlockedClick,
    onSuccess,
    onFailure = noop,
    iconOnly,
    variant,
  } = props

  const intl = useIntl()
  const mountTime = useRef<number>(new Date().getTime())
  const [isPending, setIsPending] = useState(false)
  const pendingRef = useRef(false)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    ensureFacebookSdk().then(() => {
      if (!mountedRef.current) return
      fireNonInteractionEvent({
        category: 'fb-login',
        action: 'load-script',
        label: (new Date().getTime() - mountTime.current).toString(),
      })
    }).catch(() => {})
    return () => { mountedRef.current = false }
  }, [])

  const onFacebookLogin = useCallback((data: any) => {
    if (data?.status === 'unknown') {
      // The user Probably closed the facebook window without signing in
      // https://developers.facebook.com/docs/facebook-login/web
      return
    }
    onSuccess({
      app_id: config.FACEBOOK_APP_ID,
      email: data.email,
      access_token: data.accessToken,
      givenName: data.first_name,
      surname: data.last_name,
    })
  }, [onSuccess])

  const startFacebookLogin = useCallback(() => {
    if (!window.FB?.login) {
      return
    }

    window.FB.login((response: any) => {
      if (response.authResponse) {
        window.FB.api('/me', { fields: 'first_name,last_name,email' }, (me: any) => {
          onFacebookLogin({
            ...me,
            ...response.authResponse,
          })
        })
      } else {
        onFacebookLogin({ status: response.status })
      }
    }, { scope: 'public_profile,email' })
  }, [onFacebookLogin])

  const onClick = useCallback(async() => {
    if (pendingRef.current) return

    trackClientEvent({
      subject: 'sign_up_sso_click',
      action: 'facebook_auth',
      category: 'logging',
      type: 'operational',
    })
    fireInteractionEvent({
      category: 'fb-login',
      action: 'clicked',
      label: (new Date().getTime() - mountTime.current).toString(),
    })

    if (!isFacebookSdkReady()) {
      pendingRef.current = true
      setIsPending(true)
      try {
        await waitForSocialSdk(ensureFacebookSdk())
        // A slow SDK load can outlive the click's permission to open a popup.
        // Leave the button ready for a fresh click instead of a blocked popup.
        if (!mountedRef.current || !navigator.userActivation?.isActive) return
      } catch(error) {
        if (mountedRef.current && !(error instanceof SocialSdkTimeoutError)) onFailure({ error: 'access_denied' })
        return
      } finally {
        pendingRef.current = false
        if (mountedRef.current) setIsPending(false)
      }
    }

    // ensureFacebookSdk also performs FB.init synchronously for an already-ready SDK.
    ensureFacebookSdk()
    startFacebookLogin()
  }, [onFailure, startFacebookLogin])

  return <>
    {!iconOnly && <TextButton
      {...SOCIAL_LOGIN_TEXT_BUTTON_PROPS}
      variant={variant ?? SOCIAL_LOGIN_TEXT_BUTTON_PROPS.variant}
      startIcon={isPending ? <LoadingSpinner size={20} variant="primary" /> : <LineFacebookIcon />}
      aria-busy={isPending}
      onClick={!isBlocked ? onClick : onBlockedClick}
    >
      {intl.formatMessage({
        id: 'ZZG4beRo-ZnUJDoZA_nb',
        defaultMessage: 'Facebook',
        description: 'Social login button label for Facebook sign-in in the social login button section',
      })}
    </TextButton>}
    {iconOnly && <IconButton
      {...SOCIAL_LOGIN_ICON_BUTTON_PROPS}
      variant={variant ?? SOCIAL_LOGIN_ICON_BUTTON_PROPS.variant}
      aria-busy={isPending}
      onClick={!isBlocked ? onClick : onBlockedClick}
    >
      {isPending ? <LoadingSpinner size={20} variant="primary" /> : <LineFacebookIcon />}
    </IconButton>}
  </>
}

export default FacebookLoginButton
